// Consolidated HQ billing for a multi-location client (shown on the company page, Admins/Managers).
// Enable the mode, group approved orders into a batch, then record the deposit (releases dispatch +
// starts the balance clock) and the balance (settles). Each store still gets its own invoice.
import { useEffect, useState } from 'react';
import {
  fetchCompanyBatches,
  fetchEligibleOrders,
  setCompanyBilling,
  createBatch,
  fetchBatch,
  recordBatchDeposit,
  recordBatchBalance,
  cancelBatch,
  notifyRepOfBatch,
} from '../api/billing.js';
import { fmtMoney } from '../lib/matrix.js';

const errMsg = (e) => e.response?.data?.error?.message || 'Something went wrong.';
const STATUS = {
  OPEN: { label: 'Awaiting deposit', cls: 'bg-amber-100 text-amber-700' },
  DEPOSIT_PAID: { label: 'Deposit paid · dispatched', cls: 'bg-indigo-100 text-indigo-700' },
  SETTLED: { label: 'Settled', cls: 'bg-emerald-100 text-emerald-700' },
  CANCELLED: { label: 'Cancelled', cls: 'bg-slate-100 text-slate-500' },
};
const money = (n, c) => fmtMoney(n || 0, c || 'USD');
const dateStr = (d) => (d ? new Date(d).toLocaleDateString() : '—');

// --- Record deposit / balance form (bank transfer reference) ---
function RecordForm({ label, amount, currency, onSubmit }) {
  const [reference, setReference] = useState('');
  const [receivedAt, setReceivedAt] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const submit = async () => {
    if (!reference.trim()) { setErr('Enter the bank transfer reference.'); return; }
    setBusy(true); setErr('');
    try {
      await onSubmit({ reference: reference.trim(), receivedAt: receivedAt ? new Date(receivedAt).toISOString() : undefined });
    } catch (e) { setErr(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="mt-3 rounded-md border border-line bg-surface-raised/40 p-3">
      <div className="mb-2 text-xs font-medium text-text-primary">Record {label} — {money(amount, currency)} bank transfer</div>
      <div className="flex flex-wrap items-end gap-2">
        <label className="text-[11px] text-text-muted">
          Bank reference
          <input value={reference} onChange={(e) => setReference(e.target.value)} placeholder="e.g. WIRE-20260720" className="mt-0.5 block w-48 rounded-md border border-line bg-surface px-2 py-1 text-sm" />
        </label>
        <label className="text-[11px] text-text-muted">
          Received (optional)
          <input type="date" value={receivedAt} onChange={(e) => setReceivedAt(e.target.value)} className="mt-0.5 block rounded-md border border-line bg-surface px-2 py-1 text-sm" />
        </label>
        <button onClick={submit} disabled={busy} className="rounded-md bg-accent px-3 py-1.5 text-xs font-medium text-white hover:bg-accent-hover disabled:opacity-50">
          {busy ? 'Recording…' : `Record ${label}`}
        </button>
      </div>
      {err && <div className="mt-1 text-[11px] text-error">{err}</div>}
    </div>
  );
}

// --- One batch, expandable to its statement + actions ---
function BatchRow({ batch, currency, onChanged }) {
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [busy, setBusy] = useState('');
  const [notifyMsg, setNotifyMsg] = useState('');
  const emailRep = async () => {
    setBusy('notify'); setNotifyMsg('');
    try {
      const r = await notifyRepOfBatch(batch.id);
      const pdfs = `${r.attached} PDF${r.attached === 1 ? '' : 's'}`;
      const warn = r.remittanceConfigured ? '' : ' · ⚠ add bank details in Settings → Billing';
      // Name WHO received it: with an account billing contact the statement goes to the client
      // (reps CC'd), otherwise to the rep to pass on. The button can't know before the click.
      const who = r.audience === 'client' ? 'billing contact' : 'rep';
      const cc = r.cc?.length ? ` · cc ${r.cc.join(', ')}` : '';
      setNotifyMsg(`${r.simulated ? 'Prepared (dev)' : 'Emailed'} ${r.to?.join(', ')} (${who})${cc} · ${pdfs}${warn}`);
    } catch (e) { setNotifyMsg(errMsg(e)); } finally { setBusy(''); }
  };
  const s = STATUS[batch.status] || { label: batch.status, cls: 'bg-slate-100 text-slate-500' };

  const loadDetail = async () => setDetail(await fetchBatch(batch.id).catch(() => null));
  useEffect(() => { if (open && !detail) loadDetail(); /* eslint-disable-next-line */ }, [open]);
  const refresh = async () => { await loadDetail(); onChanged?.(); };

  return (
    <div className="border-b border-line last:border-0">
      <button onClick={() => setOpen((v) => !v)} className="flex w-full items-center gap-3 px-1 py-3 text-left hover:bg-surface-raised/30">
        <span className="font-mono text-xs text-text-secondary">{batch.code}</span>
        <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${s.cls}`}>{s.label}</span>
        <span className="text-xs text-text-muted">{batch.orderCount} order{batch.orderCount === 1 ? '' : 's'}</span>
        <span className="ml-auto text-sm font-medium tabular-nums text-text-primary">{money(batch.totals.goods, currency)}</span>
        <span className="text-text-muted">{open ? '▾' : '▸'}</span>
      </button>

      {open && (
        <div className="px-1 pb-4">
          {!detail ? (
            <div className="text-xs text-text-muted">Loading…</div>
          ) : (
            <>
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="rounded-md bg-surface-raised/50 p-2">
                  <div className="text-[10px] uppercase tracking-wide text-text-muted">Deposit</div>
                  <div className="text-sm font-semibold tabular-nums">{money(detail.totals.deposit, currency)}</div>
                  <div className="text-[10px] text-text-muted">{detail.deposit.paidAt ? `paid ${dateStr(detail.deposit.paidAt)}` : 'due now'}</div>
                </div>
                <div className="rounded-md bg-surface-raised/50 p-2">
                  <div className="text-[10px] uppercase tracking-wide text-text-muted">Balance</div>
                  <div className="text-sm font-semibold tabular-nums">{money(detail.totals.balance, currency)}</div>
                  <div className="text-[10px] text-text-muted">{detail.balance.paidAt ? `paid ${dateStr(detail.balance.paidAt)}` : detail.balance.dueDate ? `due ${dateStr(detail.balance.dueDate)}` : `net ${detail.balanceNetDays} from dispatch`}</div>
                </div>
                <div className="rounded-md bg-surface-raised/50 p-2">
                  <div className="text-[10px] uppercase tracking-wide text-text-muted">Goods</div>
                  <div className="text-sm font-semibold tabular-nums">{money(detail.totals.goods, currency)}</div>
                  <div className="text-[10px] text-text-muted">{detail.orderCount} stores</div>
                </div>
              </div>

              {/* per-store lines */}
              <div className="mt-3 overflow-x-auto">
                <table className="min-w-full text-xs">
                  <thead>
                    <tr className="text-left text-[10px] uppercase tracking-wide text-text-muted">
                      <th className="py-1 pr-3 font-medium">Store · invoice</th>
                      <th className="py-1 pr-3 text-right font-medium">Deposit</th>
                      <th className="py-1 pr-3 text-right font-medium">Balance</th>
                      <th className="py-1 font-medium">State</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detail.lines.map((l) => (
                      <tr key={l.orderId} className="border-t border-line/60">
                        <td className="py-1 pr-3"><div className="text-text-primary">{l.location || l.ref}</div><div className="font-mono text-[10px] text-text-muted">{l.invoiceNumber || l.ref}</div></td>
                        <td className="py-1 pr-3 text-right tabular-nums">{l.depositPaid ? <span className="text-emerald-600">✓ {money(l.deposit, currency)}</span> : money(l.deposit, currency)}</td>
                        <td className="py-1 pr-3 text-right tabular-nums">{l.balancePaid ? <span className="text-emerald-600">✓ {money(l.balance, currency)}</span> : money(l.balance, currency)}</td>
                        {/* `released` means the Shopify order exists (i.e. it HAS gone to the warehouse).
                            This used to print "released" for deposit-paid-but-NOT-released lines — the
                            exact opposite of the truth — so staff read the state backwards. */}
                        <td className="py-1 text-[10px] text-text-muted">{l.balancePaid ? 'settled' : l.released ? 'dispatched' : l.depositPaid ? 'deposit paid' : 'held'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Email the statement — amount due + bank details + all order PDFs + summary. Goes to the
                  account's billing contact when one is set (reps CC'd), else to the assigned rep. */}
              <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-line pt-3">
                <button onClick={emailRep} disabled={busy === 'notify'} className="rounded-md border border-accent px-3 py-1.5 text-xs font-medium text-accent hover:bg-accent-light disabled:opacity-50">
                  {busy === 'notify' ? 'Emailing…' : '✉ Email statement'}
                </button>
                {notifyMsg && <span className="text-[11px] text-text-muted">{notifyMsg}</span>}
              </div>

              {/* actions by stage */}
              {detail.status === 'OPEN' && (
                <>
                  <RecordForm label="deposit" amount={detail.totals.deposit} currency={currency} onSubmit={async (b) => { await recordBatchDeposit(batch.id, b); await refresh(); }} />
                  <button
                    onClick={async () => { if (!window.confirm(`Cancel batch ${batch.code}? Its orders go back to unbatched.`)) return; setBusy('cancel'); try { await cancelBatch(batch.id); onChanged?.(); } finally { setBusy(''); } }}
                    disabled={busy === 'cancel'}
                    className="mt-2 text-[11px] text-text-muted hover:text-error"
                  >
                    {busy === 'cancel' ? '…' : 'Cancel batch'}
                  </button>
                </>
              )}
              {detail.status === 'DEPOSIT_PAID' && (
                <RecordForm label="balance" amount={detail.totals.balance} currency={currency} onSubmit={async (b) => { await recordBatchBalance(batch.id, b); await refresh(); }} />
              )}
              {detail.status === 'SETTLED' && <div className="mt-3 rounded-md bg-emerald-50 p-2 text-xs text-emerald-700">Fully settled · deposit {dateStr(detail.deposit.paidAt)} · balance {dateStr(detail.balance.paidAt)}</div>}
            </>
          )}
        </div>
      )}
    </div>
  );
}

// --- Create-batch picker ---
function CreateBatch({ companyId, onCreated, onCancel }) {
  const [orders, setOrders] = useState(null);
  const [sel, setSel] = useState({});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  useEffect(() => { fetchEligibleOrders(companyId).then(setOrders).catch(() => setOrders([])); }, [companyId]);

  const picked = Object.keys(sel).filter((k) => sel[k]);
  const currency = orders?.find((o) => sel[o.id])?.currency;
  const create = async () => {
    setBusy(true); setErr('');
    try { const b = await createBatch({ companyId, orderIds: picked }); onCreated(b); }
    catch (e) { setErr(errMsg(e)); } finally { setBusy(false); }
  };

  if (!orders) return <div className="text-xs text-text-muted">Loading orders…</div>;
  if (!orders.length) return <div className="text-xs text-text-muted">No approved, un-batched orders to group. <button onClick={onCancel} className="text-accent hover:underline">Close</button></div>;
  return (
    <div className="rounded-md border border-line bg-surface-raised/40 p-3">
      <div className="mb-2 text-xs font-medium text-text-primary">Pick orders for this batch</div>
      <div className="max-h-56 space-y-1 overflow-y-auto">
        {orders.map((o) => (
          <label key={o.id} className="flex items-center gap-2 rounded px-1 py-1 text-xs hover:bg-surface-raised">
            <input type="checkbox" checked={!!sel[o.id]} onChange={(e) => setSel({ ...sel, [o.id]: e.target.checked })} />
            <span className="text-text-primary">{o.location || o.ref}</span>
            <span className="font-mono text-[10px] text-text-muted">{o.ref}</span>
            <span className="ml-auto tabular-nums text-text-secondary">{money(o.goods, o.currency)}</span>
          </label>
        ))}
      </div>
      {err && <div className="mt-1 text-[11px] text-error">{err}</div>}
      <div className="mt-2 flex items-center gap-2">
        <button onClick={create} disabled={busy || !picked.length} className="rounded-md bg-accent px-3 py-1.5 text-xs font-medium text-white hover:bg-accent-hover disabled:opacity-50">
          {busy ? 'Creating…' : `Create batch (${picked.length})`}
        </button>
        <button onClick={onCancel} className="text-[11px] text-text-muted hover:text-text-primary">Cancel</button>
        {picked.length > 0 && <span className="text-[11px] text-text-muted">50% deposit now · 50% net-30 from dispatch{currency ? ` · ${currency}` : ''}</span>}
      </div>
    </div>
  );
}

export default function CompanyBilling({ companyId, company, onChanged }) {
  const [batches, setBatches] = useState(null);
  const [creating, setCreating] = useState(false);
  const [busy, setBusy] = useState('');
  const [consolidated, setConsolidated] = useState(!!company.consolidatedBilling);
  const [collections, setCollections] = useState(company.collectionsOwner || 'CLIENT_CONTACT');

  const load = () => fetchCompanyBatches(companyId).then(setBatches).catch(() => setBatches([]));
  useEffect(() => { if (consolidated) load(); /* eslint-disable-next-line */ }, [consolidated, companyId]);

  const saveSettings = async (next) => {
    setBusy('settings');
    try {
      const r = await setCompanyBilling(companyId, next);
      setConsolidated(r.consolidatedBilling);
      setCollections(r.collectionsOwner);
      onChanged?.();
    } finally { setBusy(''); }
  };

  return (
    <div className="rounded-lg border border-line bg-surface p-4 shadow-sm">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-text-primary">Consolidated billing</h3>
        <label className="flex items-center gap-2 text-xs text-text-secondary">
          <input type="checkbox" checked={consolidated} disabled={busy === 'settings'} onChange={(e) => saveSettings({ consolidatedBilling: e.target.checked })} />
          Bill this client as one HQ account
        </label>
      </div>

      {!consolidated ? (
        <p className="text-xs text-text-muted">
          For multi-location chains that pay once from HQ. Turn this on to group orders into a batch settled as a 50% deposit + 50% balance (net-30 from dispatch), while each store still gets its own invoice.
        </p>
      ) : (
        <>
          <div className="mb-3 flex flex-wrap items-center gap-2 border-b border-line pb-3 text-xs">
            <span className="text-text-muted">Overdue chasing:</span>
            <select
              value={collections}
              disabled={busy === 'settings'}
              onChange={(e) => saveSettings({ collectionsOwner: e.target.value })}
              className="rounded-md border border-line bg-surface px-2 py-1 text-xs"
            >
              <option value="CLIENT_CONTACT">Client billing contact</option>
              <option value="REP">Assigned rep (no accounting email)</option>
            </select>
            <button onClick={() => setCreating((v) => !v)} className="ml-auto rounded-md bg-accent px-3 py-1.5 text-xs font-medium text-white hover:bg-accent-hover">
              {creating ? 'Close' : '+ New batch'}
            </button>
          </div>

          {creating && (
            <div className="mb-3">
              <CreateBatch companyId={companyId} onCancel={() => setCreating(false)} onCreated={() => { setCreating(false); load(); onChanged?.(); }} />
            </div>
          )}

          {!batches ? (
            <div className="text-xs text-text-muted">Loading batches…</div>
          ) : batches.length === 0 ? (
            <p className="text-xs text-text-muted">No batches yet. Group this client&rsquo;s approved orders into one with “New batch”.</p>
          ) : (
            <div>
              {batches.map((b) => (
                <BatchRow key={b.id} batch={b} currency={b.currency} onChanged={() => { load(); onChanged?.(); }} />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
